import { Component, ChangeDetectionStrategy, Input, signal } from '@angular/core';

/**
 * Email address that copies itself on click, with a "Copied!" acknowledgement.
 *
 * Deliberately not a `mailto:` link (US-059): a mailto opens whatever desktop
 * mail app is configured, which for most webmail users is nothing.
 *
 * Variants:
 * - `boxed`  — bordered chip with a "Copy" / "Copied!" label (partners box, MV-369)
 * - `inline` — text + icon, "Copied!" appears beside it (footer, lead form)
 * Tone `inverse` is for navy backgrounds (footer).
 */
@Component({
  selector: 'app-copy-email',
  standalone: true,
  templateUrl: './copy-email.component.html',
  styleUrl: './copy-email.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CopyEmailComponent {
  @Input({ required: true }) email!: string;
  @Input() variant: 'boxed' | 'inline' = 'inline';
  @Input() tone: 'default' | 'inverse' = 'default';

  copied = signal(false);

  copy(): void {
    const ack = () => {
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 2000);
    };

    // navigator.clipboard requires a secure context (HTTPS or localhost).
    // On LAN-IP testing (http://192.168.x.x) the API silently rejects, so
    // fall back to the legacy textarea + execCommand path. (MV-289.)
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(this.email).then(ack).catch(() => this.fallbackCopy(this.email, ack));
    } else {
      this.fallbackCopy(this.email, ack);
    }
  }

  private fallbackCopy(text: string, onSuccess: () => void): void {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try {
      if (document.execCommand('copy')) onSuccess();
    } catch {
      /* clipboard unavailable — silent */
    }
    document.body.removeChild(ta);
  }
}
